import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { NavPanel } from '@/components/navigation/nav-rail';
import { Database, Github, HardDrive, ArrowRight, Search } from 'lucide-react';
import { Dataset } from '@/lib/dataset/types';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useDatasets } from '@/lib/datasets/use-datasets';
import { useLocalDatasets } from '@/lib/datasets/use-local-datasets';
import { useRecentlyViewed } from '@/lib/datasets/use-recently-viewed';

export function DatasetsSidePanel() {
  const pathname = usePathname();
  const [searchQuery, setSearchQuery] = useState('');
  const [recentlyViewedDatasets, setRecentlyViewedDatasets] = useState<Dataset[]>([]);
  
  // Use the proper hooks for fetching datasets
  const { 
    datasets: userDatasets, 
    isLoading: isLoadingDatasets,
    getRecentlyViewedDatasets 
  } = useDatasets();
  const { localDatasets, isLoading: isLoadingLocal } = useLocalDatasets();
  const { items: recentGithubRepos, isLoading: isLoadingGithub } = useRecentlyViewed('github', 3);
  
  // Fetch recently viewed datasets when userDatasets changes
  useEffect(() => {
    if (userDatasets.length > 0 && getRecentlyViewedDatasets) {
      getRecentlyViewedDatasets(2).then(setRecentlyViewedDatasets);
    }
  }, [userDatasets, getRecentlyViewedDatasets]);
  
  // Combine user datasets with recently viewed datasets for display
  const allDatasets = React.useMemo((): Dataset[] => {
    // Map to track which datasets have been recently viewed
    const recentViewedIds = new Set(recentlyViewedDatasets.map(d => d.id));
    
    // Start with recently viewed datasets (they have priority in ordering)
    const orderedDatasets: Dataset[] = [...recentlyViewedDatasets];
    
    // Add user datasets that aren't already in recently viewed
    const additionalUserDatasets = userDatasets.filter(dataset => !recentViewedIds.has(dataset.id));
    orderedDatasets.push(...additionalUserDatasets);
    
    // Return only first 3 datasets
    return orderedDatasets.slice(0, 3);
  }, [userDatasets, recentlyViewedDatasets]);
  
  // Helper function to format file size - keeping for potential future use
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    else return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };
  
  // Filter local datasets based on search query
  const filteredLocalDatasets = localDatasets.filter(dataset => 
    dataset.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <ScrollArea className="h-full">
      <div className="space-y-6 px-3 py-4">
        {/* Datasets section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-sm flex items-center gap-2">
              <Database className="h-4 w-4" /> Datasets
            </h3>
            <Link 
              href="/datasets#datasets-section"
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
            >
              View All <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {isLoadingDatasets ? (
            <div className="py-3 px-4 rounded-md bg-muted/30 animate-pulse h-14"></div>
          ) : allDatasets.length > 0 ? (
            <ul className="space-y-2">
              {allDatasets.map((item: Dataset) => {
                // All items in allDatasets should be proper Dataset objects now
                // since getRecentlyViewedDatasets returns full Dataset objects
                const href = `/datasets/${item.id}/${item.slug || ''}`;
                
                return (
                  <li key={item.id}>
                    <Link href={href} 
                      className="block px-3 py-2 rounded-md text-sm hover:bg-muted/50 transition-colors">
                      <div className="truncate w-[190px]" title={item.name || 'Untitled Dataset'}>
                        {item.name || 'Untitled Dataset'}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="text-xs text-muted-foreground px-3 py-2 rounded-md bg-muted/30">
              No datasets yet
            </div>
          )}
        </section>

        <Separator className="my-2" />

        {/* GitHub Datasets section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-sm flex items-center gap-2">
              <Github className="h-4 w-4" /> GitHub Datasets
            </h3>
            <Link href="/datasets/github" className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 transition-colors">
              Open <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {isLoadingGithub ? (
            <div className="py-3 px-4 rounded-md bg-muted/30 animate-pulse h-14"></div>
          ) : recentGithubRepos.length > 0 ? (
            <ul className="space-y-2">
              {recentGithubRepos.map(repo => (
                <li key={repo.id}>
                  <Link href={`/datasets/github/${repo.id}`} 
                    className="block px-3 py-2 rounded-md text-sm hover:bg-muted/50 transition-colors">
                    <div className="truncate w-[190px]" title={repo.name}>
                      {repo.name}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-muted-foreground px-3 py-2 rounded-md bg-muted/30">
              Browse GitHub repositories
            </div>
          )}
        </section>

        <Separator className="my-2" />

        {/* On My Computer section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-sm flex items-center gap-2">
              <HardDrive className="h-4 w-4" /> On My Computer
            </h3>
            <Link href="/datasets#on-my-computer" className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 transition-colors">
              View All <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {localDatasets.length > 0 && (
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs w-full"
              />
            </div>
          )}
          
          {isLoadingLocal ? (
            <div className="py-3 px-4 rounded-md bg-muted/30 animate-pulse h-14"></div>
          ) : filteredLocalDatasets.length > 0 ? (
            <ul className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {filteredLocalDatasets.map(file => (
                <li key={file.id}>
                  <Link href={`/app?file-id=${encodeURIComponent(file.id)}`} 
                    className="block px-3 py-2 rounded-md text-sm hover:bg-muted/50 transition-colors">
                    <div className="truncate w-[190px]" title={file.name}>
                      {file.name}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-muted-foreground px-3 py-2 rounded-md bg-muted/30">
              {searchQuery ? 'No matching files' : 'No files opened'}
            </div>
          )}
        </section>
      </div>
    </ScrollArea>
  );
} 